/**
 * The pure half of runtime ramp re-shading — the original's own draw-time
 * shading step (docs/formats/shading-and-paint.md §2/§3), moved from bake time
 * to frame time so a face's colour changes as its aeroplane banks.
 *
 * The bake already resolves every face's colour at one fixed reference attitude
 * (`packages/baker/src/model-paint.ts`'s `REFERENCE_SUN`) and, per §9.3, also
 * emits the three fields needed to redo the step: `anchorIndex` (the
 * post-paint, *unshaded* fill), `halfWidth` (`SHADES.DAT[anchorIndex]`) and
 * `shadeable` (`op & 0x80 && op & 0x20`). Nothing new has to be baked; this
 * module turns those fields into the buffers a GPU walk needs.
 *
 * ## What the shader has to reproduce
 *
 * ```
 * level = clamp(floor(16 · cos θ), -16, 15) + 16      // 0 .. 31
 * index = anchorIndex + shadeDelta[halfWidth - 1][level]
 * rgb   = BARON.PAL[index]
 * ```
 *
 * It is a **palette walk, not lighting**: the result is always one of the
 * `2·halfWidth` consecutive entries of the anchor's own ramp, so the hue is
 * correct by construction and the transition is banded, exactly as a 1990 VGA
 * renderer bands it. A generic Lambert term multiplied into the vertex colour
 * would be smooth, and would drift off the ramp into colours the original never
 * draws.
 *
 * ## Where the sun is, in *this* app's frame
 *
 * The engine builds the light as the constant `(0, 512, 0)` and rotates it into
 * the object's frame each draw (`RB.EXE` seg `3E4C:0022`), then dots it with
 * model-space face normals. A rotation preserves the dot product, so the same
 * number falls out of `dot(worldNormal, worldLight)` — which is what a shader
 * can compute without knowing anything about the object's pose.
 *
 * `(0, 1, 0)` is model **+Y**, i.e. nose/north, and this app's one model→world
 * convention is `(x, y, z) -> (x, z, -y)` (`world/placement-transform.ts`), so
 * the light lands on world `(0, 0, -1)`: a horizontal sun on the -Z horizon,
 * not an overhead one. That is the original's own choice and it is what makes
 * banking read — roll an aeroplane and its upper surfaces sweep through the
 * ramp instead of sitting at a constant top-lit brightness.
 */

import { shadeLevelFromCosine, SHADE_LEVEL_COUNT, SHADE_ROW_COUNT, SHIPPED_SHADE_DELTAS } from "@red-opus/formats";

/** Palette entries in a `.PAL`, hence texels in the palette lookup. */
export const PALETTE_ENTRY_COUNT = 256;
/**
 * Bias that makes the signed delta table storable in an unsigned 8-bit texture.
 * The table's range is `-8 .. +7` (`readShadeDeltaTable` validates it), so +8
 * lands every entry in `0 .. 15`.
 */
export const SHADE_DELTA_BIAS = SHADE_ROW_COUNT;

/**
 * The world-space direction of the engine's sun in this app's frame — model
 * `+Y` through `(x, y, z) -> (x, z, -y)`. See the module doc for why it is
 * horizontal.
 */
export const SUN_DIRECTION_WORLD: readonly [number, number, number] = [0, 0, -1];

/** The per-face fields the bake emits for re-shading; all optional so a `data/baked` from before §9.3 degrades to the baked colour. */
export interface ShadeableFaceColor {
  readonly colorRgba: readonly [number, number, number, number];
  readonly anchorIndex?: number;
  readonly halfWidth?: number;
  readonly shadeable?: boolean;
}

/**
 * The half-width the shader should use for a face: the baked one when the
 * engine really shades this face, and `0` — "flat, leave the baked colour
 * alone" — otherwise. Folding `shadeable` in here rather than shipping it as a
 * third attribute keeps the walk to one branch on the GPU, and it is the same
 * fold `shadedPaletteIndex` does on the CPU: an unshaded `op`, a half-width of
 * 0 and a half-width above 8 are all "draw it flat".
 */
export function effectiveHalfWidth(face: ShadeableFaceColor): number {
  if (face.shadeable !== true) return 0;
  const halfWidth = face.halfWidth ?? 0;
  return halfWidth >= 1 && halfWidth <= SHADE_ROW_COUNT ? halfWidth : 0;
}

/**
 * The two per-vertex shading attributes for a non-indexed, flat-shaded LOD,
 * repeated across each triangle's three corners so the layout matches
 * `model-geometry.ts`'s vertices exactly.
 *
 * Two single-component attributes rather than one `vec2`, for the reason
 * `screen-sprites.ts` records: `@types/three` types no swizzle accessors, so
 * reading `.x`/`.y` back off a `vec2` attribute node would only compile behind
 * a cast.
 */
export interface ShadeRampAttributes {
  /** The face's post-paint, unshaded palette index — the anchor the walk starts from. */
  readonly anchor: Float32Array;
  /** `SHADES.DAT`'s half-width, or `0` for a face the engine draws flat. */
  readonly halfWidth: Float32Array;
}

export function shadeRampAttributes(faceColors: readonly ShadeableFaceColor[]): ShadeRampAttributes {
  const anchor = new Float32Array(faceColors.length * 3);
  const halfWidth = new Float32Array(faceColors.length * 3);
  faceColors.forEach((face, triangle) => {
    const faceAnchor = face.anchorIndex ?? 0;
    const faceHalfWidth = effectiveHalfWidth(face);
    for (let corner = 0; corner < 3; corner++) {
      anchor[triangle * 3 + corner] = faceAnchor;
      halfWidth[triangle * 3 + corner] = faceHalfWidth;
    }
  });
  return { anchor, halfWidth };
}

/** True when at least one face in the LOD would actually move along a ramp — a mesh of flat colours can keep the cheaper unlit material. */
export function hasShadeableFaces(faceColors: readonly ShadeableFaceColor[]): boolean {
  return faceColors.some((face) => effectiveHalfWidth(face) > 0);
}

/**
 * The `SHADE_LEVEL_COUNT` x `SHADE_ROW_COUNT` delta table as unsigned texels,
 * row-major (x = shade level, y = half-width - 1) and biased by
 * `SHADE_DELTA_BIAS`. Read straight from `SHIPPED_SHADE_DELTAS`, which
 * `packages/formats` tests against the real `RB.EXE`.
 */
export function shadeDeltaTexels(): Uint8Array {
  const texels = new Uint8Array(SHADE_LEVEL_COUNT * SHADE_ROW_COUNT);
  for (let index = 0; index < texels.length; index++) {
    texels[index] = SHIPPED_SHADE_DELTAS[index]! + SHADE_DELTA_BIAS;
  }
  return texels;
}

/** A baked palette's JSON body (`packages/baker/src/palette-bake.ts`). */
export interface BakedPaletteBody {
  readonly colors: readonly (readonly [number, number, number])[];
}

/** A 256x1 RGBA texel row of `BARON.PAL`, so the shader's ramp walk is one dependent fetch. */
export function paletteTexels(palette: BakedPaletteBody): Uint8Array {
  const texels = new Uint8Array(PALETTE_ENTRY_COUNT * 4);
  for (let index = 0; index < PALETTE_ENTRY_COUNT; index++) {
    const colour = palette.colors[index] ?? [0, 0, 0];
    texels[index * 4] = colour[0];
    texels[index * 4 + 1] = colour[1];
    texels[index * 4 + 2] = colour[2];
    texels[index * 4 + 3] = 255;
  }
  return texels;
}

/**
 * The CPU mirror of the shader's whole walk, for tests and for anything that
 * wants one face's colour without a GPU. `cosine` is
 * `dot(worldNormal, SUN_DIRECTION_WORLD)`.
 */
export function rampShadedIndex(anchorIndex: number, halfWidth: number, cosine: number): number {
  if (halfWidth < 1 || halfWidth > SHADE_ROW_COUNT) return anchorIndex;
  const level = shadeLevelFromCosine(cosine);
  return anchorIndex + SHIPPED_SHADE_DELTAS[(halfWidth - 1) * SHADE_LEVEL_COUNT + level]!;
}
